import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, CalendarDays, LayoutGrid, Plus, Trash2, ChevronLeft, ChevronRight, ArrowRight, Megaphone } from "lucide-react";

const STAGES = [
  { key: "idea", label: "Idea" },
  { key: "draft", label: "Draft" },
  { key: "scheduled", label: "Scheduled" },
  { key: "sent", label: "Sent" },
];
const COLORS = ["#9a7b2e", "#1f2a44", "#5b6b4f", "#8a5a3c", "#2C2E33", "#7a6a9a"];

const deriveStage = (c) => c.stage || (["sent", "sending"].includes(c.status) ? "sent" : c.status === "scheduled" ? "scheduled" : "draft");
const titleOf = (c) => c.name || c.subject || "Untitled";

export default function AdminPlanner() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board");
  const [planFilter, setPlanFilter] = useState("all");
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [newPlan, setNewPlan] = useState({ name: "", color: COLORS[0] });
  const [templates, setTemplates] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/admin/campaigns`, auth),
        axios.get(`${API}/admin/marketing-plans`, auth),
      ]);
      setCampaigns(c.data.campaigns || []);
      setPlans(p.data.plans || []);
      try { const t = await axios.get(`${API}/admin/plan-templates`, auth); setTemplates(t.data.templates || []); } catch {}
    } catch (e) { if (e.response?.status === 401) adminLogout(); }
    finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const planOf = (id) => plans.find((p) => p.id === id);
  const filtered = campaigns.filter((c) => planFilter === "all" || (c.plan_id || "") === planFilter);

  const createPlan = async () => {
    if (!newPlan.name.trim()) return;
    setBusy("plan");
    try { await axios.post(`${API}/admin/marketing-plans`, newPlan, auth); setNewPlan({ name: "", color: COLORS[0] }); await load(); }
    catch {} finally { setBusy(""); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan? Campaigns stay but are unassigned.")) return;
    await axios.delete(`${API}/admin/marketing-plans/${id}`, auth); if (planFilter === id) setPlanFilter("all"); await load();
  };
  const moveStage = async (c, stage) => {
    setBusy(c.id);
    try { await axios.put(`${API}/admin/campaigns/${c.id}/stage`, { stage }, auth); await load(); }
    catch (e) { window.alert(e.response?.data?.detail || "Could not move."); } finally { setBusy(""); }
  };
  const applyTemplate = async (key) => {
    setBusy("tpl");
    try { await axios.post(`${API}/admin/marketing-plans/from-template`, { template: key }, auth); await load(); }
    catch (e) { window.alert(e.response?.data?.detail || "Could not add template."); } finally { setBusy(""); }
  };
  const onDropStage = (stage) => {
    setDragOver(null);
    const c = campaigns.find((x) => x.id === dragId);
    setDragId(null);
    if (!c) return;
    const from = deriveStage(c);
    if (from === stage) return;
    if (stage === "scheduled" || stage === "sent") {
      window.alert("Open the campaign to schedule or send it — those stages are set by real sending.");
      return;
    }
    if (from === "scheduled" || from === "sent") {
      window.alert("Sent and scheduled campaigns can't be dragged back.");
      return;
    }
    moveStage(c, stage);
  };
  const openCampaign = (c) => navigate(`/admin/campaigns?open=${c.id}`);

  const PlanBadge = ({ id }) => {
    const p = planOf(id);
    if (!p) return null;
    return <span className="inline-flex items-center gap-1 text-[10px] text-ink-muted"><span className="h-2 w-2 rounded-full" style={{ background: p.color }} /> {p.name}</span>;
  };

  const Card = ({ c }) => {
    const st = deriveStage(c);
    const draggable = st === "idea" || st === "draft";
    return (
      <div
        draggable={draggable}
        onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={() => { setDragId(null); setDragOver(null); }}
        className={`rounded-xl bg-white p-3 shadow-sm ring-1 ring-ivory-400/50 transition ${draggable ? "cursor-grab active:cursor-grabbing hover:ring-gold/40" : ""} ${dragId === c.id ? "opacity-40" : ""}`}
        data-testid={`planner-card-${c.id}`}
      >
        <button onClick={() => openCampaign(c)} className="block w-full text-left">
          <p className="font-display text-sm text-ink line-clamp-2">{titleOf(c)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <PlanBadge id={c.plan_id} />
            {c.scheduled_at && st === "scheduled" && <span className="text-[10px] text-gold-deep">{new Date(c.scheduled_at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>}
            {st === "sent" && <span className="text-[10px] text-ink-muted">{c.stats?.sent || 0} sent · {c.stats?.opens || 0} opens</span>}
          </div>
        </button>
        {st === "draft" && (
          <div className="mt-2 flex items-center gap-2 border-t border-ivory-400/40 pt-2">
            <button onClick={() => openCampaign(c)} className="ml-auto inline-flex items-center gap-1 text-[11px] text-gold-deep hover:text-ink">Schedule / send <ArrowRight className="h-3 w-3" /></button>
          </div>
        )}
      </div>
    );
  };

  // Calendar helpers
  const monthCampaigns = filtered.filter((c) => c.scheduled_at || ["sent", "sending"].includes(c.status));
  const dateKey = (c) => { const d = c.scheduled_at || c.sent_at || c.updated_at; return d ? new Date(d).toDateString() : null; };
  const daysInMonth = () => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = first.getDay();
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    return cells;
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-planner">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink"><LayoutGrid className="h-6 w-6 text-gold-deep" /> Marketing Planner</h1>
            <p className="mt-1 text-sm text-ink-soft">Group campaigns into plans, move them from idea to sent, and see your whole send calendar.</p>
          </div>
          <button onClick={() => navigate("/admin/campaigns")} data-testid="planner-new-campaign" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink">
            <Plus className="h-4 w-4" /> New campaign
          </button>
        </div>

        {/* Plans manager */}
        <div className="mt-6 rounded-2xl bg-ivory-50 p-4 ring-1 ring-ivory-400/50" data-testid="planner-plans">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setPlanFilter("all")} data-testid="planner-filter-all" className={`rounded-full px-3 py-1.5 text-xs transition ${planFilter === "all" ? "bg-ink text-ivory-50" : "bg-white text-ink-soft ring-1 ring-ivory-400/60 hover:text-ink"}`}>All campaigns</button>
            {plans.map((p) => (
              <span key={p.id} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ring-1 transition ${planFilter === p.id ? "bg-ink text-ivory-50 ring-ink" : "bg-white text-ink-soft ring-ivory-400/60"}`}>
                <button onClick={() => setPlanFilter(p.id)} data-testid={`planner-filter-${p.id}`} className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} /> {p.name} <span className="opacity-60">({p.campaign_count})</span>
                </button>
                <button onClick={() => deletePlan(p.id)} data-testid={`planner-plan-delete-${p.id}`} className="opacity-50 hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ivory-400/40 pt-3">
            <input value={newPlan.name} onChange={(e) => setNewPlan((n) => ({ ...n, name: e.target.value }))} placeholder="New plan name (e.g. Book Launch)" data-testid="planner-new-plan-name"
              className="w-56 rounded-lg border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
            <div className="flex items-center gap-1">
              {COLORS.map((col) => (
                <button key={col} onClick={() => setNewPlan((n) => ({ ...n, color: col }))} aria-label={col}
                  className={`h-6 w-6 rounded-full ring-2 transition ${newPlan.color === col ? "ring-ink" : "ring-transparent"}`} style={{ background: col }} />
              ))}
            </div>
            <button onClick={createPlan} disabled={busy === "plan" || !newPlan.name.trim()} data-testid="planner-add-plan"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm text-ivory-50 disabled:opacity-50">
              {busy === "plan" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add plan
            </button>
          </div>
          {templates.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ivory-400/40 pt-3" data-testid="planner-templates">
              <span className="text-xs text-ink-muted">Start from a template:</span>
              {templates.map((t) => (
                <button key={t.key} onClick={() => applyTemplate(t.key)} disabled={busy === "tpl"} data-testid={`planner-template-${t.key}`}
                  className="group inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs text-ink-soft ring-1 ring-ivory-400/60 transition hover:ring-gold/50 hover:text-ink disabled:opacity-50">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                  {t.name} <span className="opacity-60">· {t.count} emails</span>
                </button>
              ))}
              {busy === "tpl" && <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />}
            </div>
          )}
        </div>

        {/* View toggle */}
        <div className="mt-5 inline-flex rounded-full bg-ivory-50 p-1 ring-1 ring-ivory-400/60">
          <button onClick={() => setView("board")} data-testid="planner-view-board" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-display transition ${view === "board" ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
          <button onClick={() => setView("calendar")} data-testid="planner-view-calendar" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-display transition ${view === "calendar" ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}><CalendarDays className="h-4 w-4" /> Calendar</button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 py-6 sm:px-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : view === "board" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="planner-board">
            {STAGES.map((s) => {
              const items = filtered.filter((c) => deriveStage(c) === s.key);
              const isDrop = s.key === "idea" || s.key === "draft";
              return (
                <div key={s.key}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOver(s.key); } }}
                  onDragLeave={() => setDragOver((d) => (d === s.key ? null : d))}
                  onDrop={(e) => { e.preventDefault(); onDropStage(s.key); }}
                  className={`rounded-2xl p-3 ring-1 transition ${dragOver === s.key ? (isDrop ? "bg-gold/10 ring-gold/50" : "bg-terracotta/5 ring-terracotta/30") : "bg-ivory-50 ring-ivory-400/50"}`}
                  data-testid={`planner-col-${s.key}`}>
                  <p className="mb-3 flex items-center justify-between px-1 text-[11px] uppercase tracking-wide text-ink-muted">{s.label} <span className="rounded-full bg-ink/10 px-2 py-0.5 text-ink-soft">{items.length}</span></p>
                  <div className="space-y-2">
                    {items.map((c) => <Card key={c.id} c={c} />)}
                    {items.length === 0 && <p className="px-1 py-6 text-center text-xs text-ink-muted">{dragOver === s.key && isDrop ? "Drop here" : "Nothing here yet."}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div data-testid="planner-calendar">
            <div className="mb-3 flex items-center justify-between">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="planner-month-prev" className="rounded-full p-2 text-ink-soft hover:text-ink"><ChevronLeft className="h-5 w-5" /></button>
              <p className="font-display text-lg text-ink">{month.toLocaleDateString([], { month: "long", year: "numeric" })}</p>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="planner-month-next" className="rounded-full p-2 text-ink-soft hover:text-ink"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <p key={d} className="py-1 text-center text-[11px] uppercase tracking-wide text-ink-muted">{d}</p>)}
              {daysInMonth().map((day, i) => {
                const items = day ? monthCampaigns.filter((c) => dateKey(c) === day.toDateString()) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`min-h-[86px] rounded-lg p-1.5 ${day ? "bg-ivory-50 ring-1 ring-ivory-400/40" : ""} ${isToday ? "ring-2 ring-gold/50" : ""}`}>
                    {day && <p className="mb-1 text-[11px] text-ink-muted">{day.getDate()}</p>}
                    <div className="space-y-1">
                      {items.map((c) => {
                        const p = planOf(c.plan_id);
                        const sent = ["sent", "sending"].includes(c.status);
                        return (
                          <button key={c.id} onClick={() => openCampaign(c)} data-testid={`planner-cal-${c.id}`}
                            className="block w-full truncate rounded px-1.5 py-1 text-left text-[11px] text-white" style={{ background: p?.color || (sent ? "#5b6b4f" : "#1f2a44") }} title={titleOf(c)}>
                            {sent ? "✓ " : ""}{titleOf(c)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-muted"><Megaphone className="h-3.5 w-3.5" /> Scheduled and sent campaigns show on their dates. Tap any to open it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
